import { useMemo, useRef, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import api_endpoints from "../../../api";
import { useFailAlert } from "../../../context/FailAlertContext/FailAlertContext";
import { useSuccessAlert } from "../../../context/SuccessAlertContext/SuccessAlertContext";

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  return error?.message || fallback;
};

const getStatusColor = (status) => {
  if (status === "New") return "success";
  if (status === "Updated") return "warning";
  return "default";
};

export default function TranslationCsvActions({ platform, onImported, sx = {} }) {
  const fileInputRef = useRef(null);
  const { setMessageSuccessAlert, setShowSuccessAlert } = useSuccessAlert();
  const { setMessageAndShow } = useFailAlert();

  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [importPayload, setImportPayload] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);

  const hasImportableRows = useMemo(
    () => previewRows.some((row) => row.status === "New" || row.status === "Updated"),
    [previewRows]
  );

  const showSuccess = (message) => {
    setMessageSuccessAlert(message);
    setShowSuccessAlert(true);
  };

  const handleExport = async () => {
    setBusy(true);

    try {
      const response = await api_endpoints.exportTranslationsCsv(platform);
      const date = new Date().toISOString().slice(0, 10);
      downloadBlob(response.data, `translations-${platform}-${date}.csv`);
      showSuccess("Translations exported successfully");
    } catch (error) {
      setMessageAndShow(getErrorMessage(error, "Failed to export translations"));
    } finally {
      setBusy(false);
    }
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setBusy(true);

    try {
      const csv = await file.text();
      const payload = { csv };
      const response = await api_endpoints.previewTranslationsCsv(payload);

      setImportPayload(payload);
      setPreviewRows(response.data?.rows || []);
      setPreviewOpen(true);
    } catch (error) {
      setMessageAndShow(getErrorMessage(error, "Invalid translations CSV"));
    } finally {
      setBusy(false);
    }
  };

  const handleClosePreview = () => {
    if (busy) return;
    setPreviewOpen(false);
    setImportPayload(null);
    setPreviewRows([]);
  };

  const handleConfirmImport = async () => {
    if (!importPayload) return;

    setBusy(true);

    try {
      await api_endpoints.importTranslationsCsv(importPayload);
      handleClosePreview();
      showSuccess("Translations imported successfully");
      onImported?.();
    } catch (error) {
      setMessageAndShow(getErrorMessage(error, "Failed to import translations"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 2, ...sx }}>
        <Button variant="outlined" onClick={handleExport} disabled={busy}>
          Export
        </Button>
        <Button variant="contained" onClick={() => fileInputRef.current?.click()} disabled={busy}>
          Import
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="text/csv,.csv"
          hidden
          onChange={handleFileSelected}
        />
      </Stack>

      <Dialog open={previewOpen} onClose={handleClosePreview} fullWidth maxWidth="lg">
        <DialogTitle>Preview translation import</DialogTitle>
        <DialogContent dividers>
          <TableContainer sx={{ maxHeight: 560, border: "1px solid #e0e0e0" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 90 }}>Platform</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>Page</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>Key</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Language</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!previewRows.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No new or updated translation values were found.
                    </TableCell>
                  </TableRow>
                )}
                {previewRows.map((row, index) => (
                  <TableRow key={`${row.platform}-${row.page}-${row.key}-${row.language}-${index}`}>
                    <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                      {row.platform}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.page}
                        </Typography>
                        {row.is_new_page && <Chip size="small" color="success" label="New page" />}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>{row.key}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>{row.language}</TableCell>
                    <TableCell sx={{ whiteSpace: "pre-wrap", verticalAlign: "top" }}>{row.value}</TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Chip size="small" color={getStatusColor(row.status)} label={row.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmImport} disabled={busy || !hasImportableRows}>
            Confirm import
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
